import { NavLink, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LangContext";
import { NotificationBell } from "@/components/NotificationBell";
import {
  LayoutDashboard, ShoppingCart, Package, Users, BarChart3, LogOut, Heart,
  MessageCircle, Settings, Globe, ClipboardList, PanelLeftClose,
  PanelLeftOpen, Languages, Truck, ShoppingBag, Store, TrendingUp, Paintbrush,
  Gift, Phone, Maximize2, Minimize2, Download, X, Lock, BookOpen, CalendarCheck, Sparkles, RotateCcw
} from "lucide-react";
import { toast } from "sonner";

let deferredInstallPrompt = null;
if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => { e.preventDefault(); deferredInstallPrompt = e; });
}

const LOGO_URL = "/logo_compact.png";

const NAV_SECTIONS = [
  { label: "Main", items: [
    { path: "/", labelKey: "nav_dashboard", icon: LayoutDashboard, permKey: null, roles: ["tenant_owner", "manager"] },
    { path: "/pos", labelKey: "nav_pos", icon: ShoppingCart, permKey: "pos", roles: ["tenant_owner", "manager", "cashier", "inventory_staff"] },
    { path: "/orders", labelKey: "nav_orders", icon: ShoppingBag, permKey: null, moduleKey: "shop", roles: ["tenant_owner", "manager"] },
    { path: "/returns", labelKey: "nav_returns", icon: RotateCcw, permKey: "pos", roles: ["tenant_owner", "manager", "cashier"] },
    { path: "/whatsapp-orders", labelKey: "nav_wa_orders", icon: MessageCircle, permKey: null, moduleKey: "whatsapp", roles: ["tenant_owner", "manager"] },
  ]},
  { label: "Store", items: [
    { path: "/inventory", labelKey: "nav_inventory", icon: Package, permKey: "inventory", roles: ["tenant_owner", "manager", "inventory_staff"] },
    { path: "/shipments", labelKey: "nav_shipments", icon: ShoppingBag, permKey: "inventory", moduleKey: "shipments", roles: ["tenant_owner", "manager", "inventory_staff"] },
    { path: "/customers", labelKey: "nav_customers", icon: Users, permKey: "customers", roles: ["tenant_owner", "manager", "cashier"] },
    { path: "/loyalty", labelKey: "nav_loyalty", icon: Heart, permKey: "customers", moduleKey: "loyalty", roles: ["tenant_owner", "manager"] },
    { path: "/vendors", labelKey: "nav_vendors", icon: Truck, permKey: null, moduleKey: "suppliers", roles: ["tenant_owner", "manager"] },
    { path: "/shop", labelKey: "nav_shop", icon: Store, permKey: null, moduleKey: "shop", roles: ["tenant_owner", "manager"] },
    { path: "/catalog", labelKey: "nav_catalog", icon: BookOpen, permKey: null, moduleKey: "shop", roles: ["tenant_owner", "manager"] },
    { path: "/ai-studio", labelKey: "nav_ai_studio", icon: Sparkles, permKey: null, roles: ["tenant_owner", "manager"] },
    { path: "/shop-customizer", labelKey: "nav_shop_customizer", icon: Paintbrush, permKey: null, moduleKey: "shop", roles: ["tenant_owner", "manager"] },
  ]},
  { label: "Team", items: [
    { path: "/staff", labelKey: "nav_staff", icon: Users, permKey: "staff", roles: ["tenant_owner", "manager"] },
    { path: "/my-work", labelKey: "hr_my_work", icon: CalendarCheck, permKey: null, roles: ["tenant_owner", "manager", "cashier", "inventory_staff"] },
  ]},
  { label: "Analytics", items: [
    { path: "/reports", labelKey: "nav_reports", icon: BarChart3, permKey: "reports", roles: ["tenant_owner", "manager"] },
    { path: "/analytics", labelKey: "nav_analytics", icon: TrendingUp, permKey: "reports", moduleKey: "analytics", roles: ["tenant_owner", "manager"] },
    { path: "/audit-log", labelKey: "nav_audit", icon: ClipboardList, permKey: "audit_log", roles: ["tenant_owner"] },
  ]},
  { label: "Operations", items: [
    { path: "/whatsapp", labelKey: "nav_whatsapp", icon: Phone, permKey: null, moduleKey: "whatsapp", roles: ["tenant_owner", "manager"] },
    { path: "/coupons", labelKey: "nav_coupons", icon: Gift, permKey: null, moduleKey: "coupons", roles: ["tenant_owner", "manager"] },
    { path: "/messages", labelKey: "nav_messages", icon: MessageCircle, permKey: null, moduleKey: "messages", roles: ["tenant_owner", "manager"] },
    { path: "/social", labelKey: "nav_social", icon: Globe, permKey: null, moduleKey: "social", roles: ["tenant_owner", "manager"] },
  ]},
  { label: "System", items: [
    { path: "/settings", labelKey: "nav_settings", icon: Settings, permKey: "settings", roles: ["tenant_owner"] },
  ]},
];

// Flat list for mobile bottom bar
const ALL_ITEMS = NAV_SECTIONS.flatMap(s => s.items);

function useFullscreen() {
  const [isFull, setIsFull] = useState(false);
  useEffect(() => {
    const onChange = () => setIsFull(!!(document.fullscreenElement || document.webkitFullscreenElement));
    document.addEventListener("fullscreenchange", onChange);
    document.addEventListener("webkitfullscreenchange", onChange);
    return () => { document.removeEventListener("fullscreenchange", onChange); document.removeEventListener("webkitfullscreenchange", onChange); };
  }, []);
  const toggle = () => {
    try {
      if (document.fullscreenElement || document.webkitFullscreenElement) {
        (document.exitFullscreen || document.webkitExitFullscreen)?.call(document);
      } else {
        const el = document.documentElement;
        (el.requestFullscreen || el.webkitRequestFullscreen)?.call(el);
      }
    } catch { /* unsupported browser */ }
  };
  return [isFull, toggle];
}

export function Sidebar({ onCollapseChange, forceCollapsed }) {
  const { user, logout, tenant, branding, hrAccess } = useAuth();
  const { t, lang, toggleLang } = useLang();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(() => { try { return localStorage.getItem("sidebar_collapsed") === "1"; } catch { return false; } });
  const [hovered, setHovered] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [rolePerms, setRolePerms] = useState(null);
  const [isFull, toggleFullscreen] = useFullscreen();
  const isStandalone = (typeof window !== "undefined") && (window.matchMedia?.("(display-mode: standalone)")?.matches || window.navigator.standalone === true);
  const handleInstall = async () => {
    if (deferredInstallPrompt) {
      deferredInstallPrompt.prompt();
      await deferredInstallPrompt.userChoice;
      deferredInstallPrompt = null;
    } else if (/iP(hone|ad|od)/.test(navigator.userAgent)) {
      toast.info("Tap the Share button in Safari, then choose 'Add to Home Screen' to install the app", { duration: 8000 });
    } else {
      toast.info("Open your browser menu (⋮) and choose 'Install app' / 'Add to Home screen'", { duration: 8000 });
    }
  };
  const [enabledModules, setEnabledModules] = useState(null); // null = loading, {} = no restrictions

  const brandLogoUrl = branding?.logo_url || "";
  const logoUrl = brandLogoUrl
    ? (brandLogoUrl.startsWith("http") ? brandLogoUrl : `${process.env.REACT_APP_BACKEND_URL}${brandLogoUrl}`)
    : LOGO_URL;
  const storeName = tenant?.name || t("store_name");

  useEffect(() => {
    if (forceCollapsed !== undefined) { setCollapsed(forceCollapsed); if (onCollapseChange) onCollapseChange(forceCollapsed); }
  }, [forceCollapsed, onCollapseChange]);

  // Report persisted initial state to layout on mount
  useEffect(() => { if (onCollapseChange) onCollapseChange(collapsed); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  useEffect(() => {
    const opts = { withCredentials: true };
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/config`, opts)
      .then(r => { if (r.data.role_permissions) setRolePerms(r.data.role_permissions); }).catch(() => {});
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/tenant/settings/my-modules`, opts)
      .then(r => { setEnabledModules(r.data.modules || {}); }).catch(() => { setEnabledModules({}); });
  }, []);

  const isVisible = !collapsed || hovered;
  const handleToggle = () => {
    const v = !collapsed;
    setCollapsed(v); setHovered(false);
    try { localStorage.setItem("sidebar_collapsed", v ? "1" : "0"); } catch { /* ignore */ }
    if (onCollapseChange) onCollapseChange(v);
  };

  const isLocked = (item) => {
    const modKey = item.moduleKey || item.permKey;
    return !!(enabledModules && Object.keys(enabledModules).length > 0 && modKey && enabledModules[modKey] === false);
  };

  const canSee = (item) => {
    const role = user?.role_code;
    if (item.path === "/pos" && hrAccess && hrAccess.pos === false) return false;   // cashier access switched off for this account
    if (role === "tenant_owner") return true;
    if (item.permKey && rolePerms && rolePerms[role]) return rolePerms[role][item.permKey] === true;
    return item.roles.includes(role);
  };

  const filteredSections = NAV_SECTIONS.map(s => ({ ...s, items: s.items.filter(canSee) })).filter(s => s.items.length > 0);
  const visibleItems = ALL_ITEMS.filter(canSee);
  const mobileItems = visibleItems.filter(i => !isLocked(i)).slice(0, 4);
  const moreItems = visibleItems.filter(i => !mobileItems.includes(i));

  return (
    <>
      {/* ═══ MOBILE: Bottom Tab Bar ═══ */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-th-card border-t border-th-border flex items-center justify-around px-1 pt-1 pb-1 safe-bottom" style={{ paddingBottom: 'max(4px, env(safe-area-inset-bottom))' }}>
        {mobileItems.map(item => {
          const Icon = item.icon;
          const active = location.pathname === item.path;
          return (
            <NavLink key={item.path} to={item.path}
              className={`flex flex-col items-center gap-0.5 px-3 py-1.5 rounded-lg min-w-[56px] transition-colors ${active ? "text-th-accent" : "text-th-muted"}`}>
              <Icon className="w-5 h-5" />
              <span className="text-[9px] font-medium leading-none">{t(item.labelKey)}</span>
            </NavLink>
          );
        })}
        <button onClick={() => setMobileOpen(!mobileOpen)} className={`flex flex-col items-center gap-0.5 px-3 py-1.5 rounded-lg min-w-[56px] transition-colors ${mobileOpen ? "text-th-accent" : "text-th-muted"}`}>
          <Settings className="w-5 h-5" />
          <span className="text-[9px] font-medium leading-none">More</span>
        </button>
      </div>

      {/* ═══ MOBILE: More Menu ═══ */}
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/50" onClick={() => setMobileOpen(false)} />
          <div className="absolute bottom-0 left-0 right-0 bg-th-card border-t border-th-border rounded-t-2xl p-4 space-y-1 animate-slide-in max-h-[70vh] overflow-y-auto" style={{ paddingBottom: 'max(80px, calc(80px + env(safe-area-inset-bottom)))' }}>
            <div className="w-10 h-1 rounded-full bg-th-border mx-auto mb-1" />
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-bold text-th-dim uppercase tracking-wider">{t("nav_more") || "Menu"}</p>
              <button onClick={() => setMobileOpen(false)} data-testid="mobile-menu-close" aria-label="Close menu"
                className="w-9 h-9 flex items-center justify-center rounded-full bg-th-surface text-th-muted hover:text-cream-900">
                <X className="w-4 h-4" />
              </button>
            </div>
            {moreItems.map(item => {
              const Icon = item.icon;
              const active = location.pathname === item.path;
              const locked = isLocked(item);
              return (
                <NavLink key={item.path} to={item.path}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors ${active ? "bg-th-accent text-white" : "text-th-muted hover:bg-th-surface"} ${locked ? "opacity-55" : ""}`}>
                  <Icon className="w-5 h-5" /><span>{t(item.labelKey)}</span>
                  {locked && <Lock className="w-3.5 h-3.5 text-th-dim" style={{ marginInlineStart: "auto" }} />}
                </NavLink>
              );
            })}
            <Separator />
            <div className="flex gap-2 pt-2">
              <button onClick={toggleLang} className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg bg-th-surface text-th-muted text-sm font-bold">
                <Languages className="w-4 h-4" />{lang === "en" ? "العربية" : "English"}
              </button>
              <button onClick={toggleFullscreen} data-testid="mobile-fullscreen-toggle" className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg bg-th-surface text-th-muted text-sm font-bold">
                {isFull ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}{isFull ? "Exit Full" : "Full Screen"}
              </button>
            </div>
            {!isStandalone && (
              <button onClick={handleInstall} data-testid="install-app-btn" className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg bg-clay-50 text-th-accent text-sm font-bold">
                <Download className="w-4 h-4" /> Install App on Home Screen
              </button>
            )}
            <button onClick={logout} className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg text-red-400 hover:bg-red-500/10 text-sm font-medium mt-1">
              <LogOut className="w-4 h-4" />{t("logout")}
            </button>
          </div>
        </div>
      )}

      {/* ═══ DESKTOP: Sidebar (icon rail when collapsed, hover to peek) ═══ */}
      <aside
        className={`hidden md:flex fixed left-0 top-0 h-screen bg-th-card border-r border-th-border flex-col z-30 transition-[width] duration-200 ease-in-out ${isVisible ? "w-60" : "w-14"} ${collapsed && hovered ? "shadow-xl" : ""}`}
        onMouseEnter={() => { if (collapsed) setHovered(true); }}
        onMouseLeave={() => { if (collapsed) setHovered(false); }}
        data-testid="sidebar">

        {/* Brand */}
        <div className={`py-3.5 border-b border-th-border flex items-center ${isVisible ? "px-4 justify-between" : "px-0 flex-col gap-2 justify-center"}`}>
          <div className={`flex items-center gap-2.5 ${isVisible ? "" : "justify-center"}`}>
            <img
              src={logoUrl}
              alt="Logo"
              className="w-8 h-8 rounded-lg object-cover"
              onError={(e) => { e.target.src = LOGO_URL; }}
            />
            {isVisible && (
              <div>
                <p className="text-sm font-bold text-cream-900 leading-tight font-heading">{storeName}</p>
                <p className="text-[9px] text-th-dim font-medium tracking-wider uppercase">
                  {branding?.tagline || t("store_tag")}
                </p>
              </div>
            )}
          </div>
          <div className={`flex items-center gap-1 ${isVisible ? "" : "flex-col"}`}>
            {isVisible && <NotificationBell />}
            {isVisible && (
              <button onClick={toggleFullscreen} data-testid="fullscreen-toggle" className="p-1 rounded-md text-th-dim hover:text-cream-900 hover:bg-th-surface transition-colors" title={isFull ? "Exit full screen" : "Full screen"}>
                {isFull ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
              </button>
            )}
            <button onClick={handleToggle} data-testid="sidebar-collapse-toggle" className="p-1 rounded-md text-th-dim hover:text-cream-900 hover:bg-th-surface transition-colors" title={collapsed ? t("pin_sidebar") : t("auto_hide")}>
              {collapsed ? <PanelLeftOpen className="w-4 h-4" /> : <PanelLeftClose className="w-4 h-4" />}
            </button>
          </div>
        </div>

        {/* Navigation */}
        <nav className={`flex-1 overflow-y-auto py-3 space-y-4 ${isVisible ? "px-2" : "px-1.5"}`}>
          {filteredSections.map(section => (
            <div key={section.label}>
              {isVisible ? (
                <p className="px-3 mb-1.5 text-[9px] font-bold text-th-dim uppercase tracking-widest">{section.label}</p>
              ) : (
                <div className="h-px bg-th-border mx-1 mb-1.5" />
              )}
              <div className="space-y-0.5">
                {section.items.map(item => {
                  const Icon = item.icon;
                  const active = location.pathname === item.path;
                  const locked = isLocked(item);
                  return (
                    <NavLink key={item.path} to={item.path}
                      data-testid={`nav-${item.labelKey.replace("nav_", "")}`}
                      title={isVisible ? undefined : t(item.labelKey)}
                      style={{ borderInlineStart: active && isVisible ? '3px solid var(--th-accent)' : '3px solid transparent' }}
                      className={`flex items-center gap-2.5 py-2 rounded-lg text-[13px] font-medium transition-colors duration-150 ${isVisible ? "px-3" : "px-0 justify-center"} ${
                        active
                          ? "bg-clay-50 text-th-accent font-semibold"
                          : "text-th-muted hover:bg-th-surface hover:text-cream-900"
                      } ${locked ? "opacity-55" : ""}`}>
                      <Icon className="w-[17px] h-[17px] flex-shrink-0" />
                      {isVisible && <span className="truncate">{t(item.labelKey)}</span>}
                      {isVisible && locked && <Lock className="w-3 h-3 flex-shrink-0 text-th-dim" style={{ marginInlineStart: "auto" }} data-testid={`nav-lock-${item.labelKey.replace("nav_", "")}`} />}
                    </NavLink>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>

        {/* Footer */}
        <div className="border-t border-th-border p-2 space-y-2">
          {isVisible ? (
            <>
              {/* Language */}
              <button onClick={toggleLang} data-testid="lang-toggle"
                className="w-full flex items-center justify-center gap-1.5 py-1.5 rounded-lg bg-th-surface text-th-muted hover:text-cream-900 text-xs font-bold transition-colors">
                <Languages className="w-3.5 h-3.5" />{lang === "en" ? "العربية" : "English"}
              </button>

              {/* User */}
              <div className="flex items-center gap-2 px-2 py-1.5">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 bg-clay-50">
                  <span className="text-xs font-bold text-th-accent">{(user?.full_name || user?.username || "?").charAt(0)}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-cream-900 truncate">{user?.full_name || user?.username}</p>
                  <p className="text-[10px] text-th-dim capitalize">{(user?.role_code || "").replace(/_/g, " ")}</p>
                </div>
                <button onClick={logout} data-testid="logout-button" title={t("logout")}
                  className="p-1.5 rounded-md text-th-dim hover:text-red-400 hover:bg-red-500/10 transition-colors">
                  <LogOut className="w-3.5 h-3.5" />
                </button>
              </div>
            </>
          ) : (
            <div className="flex flex-col items-center gap-1.5">
              <button onClick={toggleFullscreen} data-testid="fullscreen-toggle-rail" title={isFull ? "Exit full screen" : "Full screen"}
                className="p-1.5 rounded-md text-th-dim hover:text-cream-900 hover:bg-th-surface transition-colors">
                {isFull ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
              </button>
              <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-clay-50" title={user?.full_name || user?.username}>
                <span className="text-xs font-bold text-th-accent">{(user?.full_name || user?.username || "?").charAt(0)}</span>
              </div>
              <button onClick={logout} data-testid="logout-button" title={t("logout")}
                className="p-1.5 rounded-md text-th-dim hover:text-red-400 hover:bg-red-500/10 transition-colors">
                <LogOut className="w-3.5 h-3.5" />
              </button>
            </div>
          )}
        </div>
      </aside>
    </>
  );
}

function Separator() {
  return <div className="h-px bg-th-border my-2" />;
}
